import { storeToRefs } from 'pinia';
import { useSessionStore } from '@Stores';

export type PendingAuthRoute = '/password-expired' | '/2fa/readiness' | '/2fa/challenge' | null;

type TwoFactorChannel = 'email' | 'sms';

function normalizeChannels(source: unknown): TwoFactorChannel[] {
  if (!Array.isArray(source)) return ['email'];

  const channels = source.filter((channel): channel is TwoFactorChannel => channel === 'email' || channel === 'sms');
  return channels.length ? channels : ['email'];
}

function recoverPendingAuthRoute(error: any): PendingAuthRoute {
  const { pendingPasswordExpired, pendingTwoFa, pendingTwoFaReadiness } = storeToRefs(useSessionStore());
  const payload = error?.response?.data?.data || error?.response?.data || null;
  const status = String(payload?.status || '').trim();
  if (!status) return null;

  if (status === 'password_expired') {
    pendingTwoFa.value = null;
    pendingTwoFaReadiness.value = null;
    pendingPasswordExpired.value = {
      email: String(payload?.email || '')
        .trim()
        .toLowerCase(),
    };

    return '/password-expired';
  }

  if (status === '2fa_readiness_required') {
    const availableChannels = normalizeChannels(
      payload?.available_channels || payload?.channels || [payload?.channel_mode]
    );
    pendingPasswordExpired.value = null;
    pendingTwoFa.value = null;
    pendingTwoFaReadiness.value = {
      channel_mode: payload?.channel_mode || 'email',
      channels: availableChannels,
      available_channels: availableChannels,
      email: String(payload?.email || '')
        .trim()
        .toLowerCase(),
      phone: String(payload?.phone || '').trim(),
    };

    return '/2fa/readiness';
  }

  if (status === '2fa_required' && payload?.challenge_id) {
    const availableChannels = normalizeChannels(payload?.available_channels || payload?.channels || ['email']);
    pendingPasswordExpired.value = null;
    pendingTwoFaReadiness.value = null;
    pendingTwoFa.value = {
      challenge_id: payload.challenge_id,
      channel_mode: payload.channel_mode || 'email',
      channels: availableChannels,
      available_channels: availableChannels,
      selected_channel:
        payload?.selected_channel === 'email' || payload?.selected_channel === 'sms' ? payload.selected_channel : null,
      email: String(payload?.email || '').trim(),
      phone: String(payload?.phone || '').trim(),
      trusted_device_available: payload?.trusted_device_available === true,
      trusted_days: Number(payload?.trusted_days || 0),
    };

    return '/2fa/challenge';
  }

  return null;
}

function isOnPendingAuthRoute(pathname: string, pendingRoute: PendingAuthRoute): boolean {
  if (!pendingRoute) return false;

  return pathname === pendingRoute || pathname.startsWith(`${pendingRoute}/`);
}

function redirectFromAuthPreconditionError(
  error: any,
  navigate: (path: Exclude<PendingAuthRoute, null>) => Promise<unknown>
): Promise<boolean> {
  const pendingRoute = recoverPendingAuthRoute(error);
  if (!pendingRoute) {
    return Promise.resolve(false);
  }

  if (isOnPendingAuthRoute(window.location.pathname, pendingRoute)) {
    return Promise.resolve(false);
  }

  return navigate(pendingRoute).then(() => true);
}

export {
  isOnPendingAuthRoute,
  recoverPendingAuthRoute,
  redirectFromAuthPreconditionError,
}
